/**
 * AddHotspotModal — B8.1
 *
 * Modal zum Hinzufügen eines Hotspots per Solana-Adresse oder Name.
 * Validierung: Solana-Adresse (32–44 alphanumerische Zeichen ohne 0/O/I/l)
 * oder Name (beliebige Zeichenkette, mind. 3 Zeichen).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { useAddHotspot } from './useAddHotspot'
import { useHotspots } from './useHotspots'
import { useHotspotSearch } from './useHotspotSearch'
import { toast } from '../../components/Toast/useToast'
import { quotaInfoFromError, type QuotaInfo } from './quotaError'

type Props = {
  isOpen: boolean
  onClose: () => void
}

const RESULT_ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 10,
  flexWrap: 'wrap',
  marginBottom: 8,
}

type FormError = {
  name_or_address?: string
  server?: string
}

// Solana Wallet-Adresse: 32–44 Zeichen, Base58-Alphabet (fuer Vollstaendigkeit,
// falls jemand versehentlich eine Wallet- statt Hotspot-Adresse eingibt).
const SOLANA_ADDRESS_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/
// WP5 (Deep-Review 04.09.): das Backend akzeptiert in
// resolve_hotspot_address inzwischen size.between?(32, 512), konsistent zu
// hotspot_mixin/guard.rb (/\A[1-9A-HJ-NP-Za-km-z]{32,512}\z/). Die zuvor hier
// hart codierte 50-52-Grenze (onboarding-wallet-hotspot Finding #4) lehnte
// jede Adresse ausserhalb dieses schmalen Fensters clientseitig ab, obwohl
// das Backend seit dem Post-Solana-Format ein deutlich breiteres Spektrum
// gueltiger Hotspot-Adressen zulaesst — auf 32-512 Zeichen gelockert, damit
// die FE-Validierung den echten Backend-Vertrag widerspiegelt.
const HOTSPOT_ADDRESS_RE = /^[1-9A-HJ-NP-Za-km-z]{32,512}$/

type ValidationErrorKey =
  | 'add.errors.required'
  | 'add.errors.tooShort'
  | 'add.errors.invalidAddress'

function validateInputKey(value: string): ValidationErrorKey | undefined {
  const trimmed = value.trim()

  if (!trimmed) {
    return 'add.errors.required'
  }

  if (trimmed.length < 3) {
    return 'add.errors.tooShort'
  }

  // Wenn es wie eine Adresse aussieht (>=32 Zeichen), strenger validieren —
  // akzeptiert sowohl Wallet- (32-44) als auch Hotspot-Adressformat (50-52).
  if (trimmed.length >= 32) {
    if (!SOLANA_ADDRESS_RE.test(trimmed) && !HOTSPOT_ADDRESS_RE.test(trimmed)) {
      return 'add.errors.invalidAddress'
    }
  }

  return undefined
}

function serverErrorKey(
  err: unknown,
): 'add.errors.conflict' | 'add.errors.notFound' | 'add.errors.generic' | string {
  if (err && typeof err === 'object') {
    const anyErr = err as Record<string, unknown>

    // Form 1: { error: { code, message } } (v2-Envelope)
    if (anyErr.error && typeof anyErr.error === 'object') {
      const errObj = anyErr.error as Record<string, unknown>
      const code = errObj.code as string | undefined

      if (code === 'conflict') return 'add.errors.conflict'
      if (code === 'not_found') return 'add.errors.notFound'
    }

    // Form 2: { success: false, errors: [...] } (Api::V2::HotspotsController#create,
    // 422 bei unaufloesbarem name_or_address). BE liefert hier keinen Code, nur
    // Freitext-Messages — 'Hotspot not found' ist die einzige, die wir gezielt
    // auf einen eigenen Text mappen.
    if (Array.isArray(anyErr.errors)) {
      if (
        anyErr.errors.some(
          (e) => typeof e === 'string' && e.toLowerCase().includes('hotspot not found'),
        )
      ) {
        return 'add.errors.notFound'
      }
    }
  }
  return 'add.errors.generic'
}

export function AddHotspotModal({ isOpen, onClose }: Props) {
  const { t } = useTranslation('hotspots')
  const [nameOrAddress, setNameOrAddress] = useState('')
  const [errors, setErrors] = useState<FormError>({})
  const [quota, setQuota] = useState<QuotaInfo | null>(null)

  // SPEC.md F1: Eingabe -> Trefferschritt -> pro Treffer "Add to account"/"Watch".
  const [submitted, setSubmitted] = useState('')
  // BLOCKER #30: mehrere Zeilen (Suchtreffer + Freitext-Kandidat) koennen
  // unabhaengig voneinander "done" sein -> Schluessel ist die jeweilige
  // Adresse/der jeweilige Name-or-address-Wert der Zeile.
  const [done, setDone] = useState<Record<string, 'added' | 'watched'>>({})

  const addHotspot = useAddHotspot()
  // BLOCKER #30: globale Netzwerksuche (nicht user-scoped) — anders als die
  // fruehere useGlobalSearch muss dieser Endpunkt auch fremde, noch nicht
  // geclaimte Hotspots finden, sonst liesse sich im Add-Flow nichts finden.
  const search = useHotspotSearch(submitted)
  const ownHotspots = useHotspots({ per_page: 500 }, isOpen && submitted.length >= 3)

  function handleClose() {
    setNameOrAddress('')
    setErrors({})
    setQuota(null)
    setSubmitted('')
    setDone({})
    addHotspot.reset()
    onClose()
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const errorKey = validateInputKey(nameOrAddress)
    if (errorKey) {
      setErrors({ name_or_address: t(errorKey) })
      return
    }

    setErrors({})
    setQuota(null)
    setDone({})
    setSubmitted(nameOrAddress.trim())
  }

  async function handleAdd(watch: boolean, key: string, target: string) {
    setErrors({})
    setQuota(null)
    try {
      await addHotspot.mutateAsync({ name_or_address: target, watch })
      setDone((prev) => ({ ...prev, [key]: watch ? 'watched' : 'added' }))
      toast.success(watch ? t('add.results.watchToast') : t('add.results.addToast'))
    } catch (err) {
      // 402 quota_exceeded => Upsell statt generischem Fehlertext (Story 6.1 AC3).
      const quotaInfo = quotaInfoFromError(err)
      if (quotaInfo) {
        setQuota(quotaInfo)
        return
      }
      setErrors({ server: t(serverErrorKey(err)) })
    }
  }

  const isAddressQuery = submitted.length >= 32
  // `watched` kommt aus der eigenen Hotspot-Liste (owned=true-Datensatz mit
  // watched-Unterflag) — jeder dort gefundene Treffer ist bereits im Konto.
  const ownStatusById = new Map(
    (ownHotspots.data ?? []).map(
      (h) => [String(h.id), h.watched === true ? 'watched' : 'owned'] as const,
    ),
  )
  const hits = search.data ?? []
  const lowered = submitted.toLowerCase()
  const exactHit = hits.some(
    (h) => h.name?.toLowerCase() === lowered || h.address?.toLowerCase() === lowered,
  )
  // Der Freitext-Fallback-Kandidat erscheint NUR, wenn die Eingabe wie eine
  // Adresse aussieht (>=32 Zeichen) ODER die Suche 0 Treffer liefert — sonst
  // waere er ein Fantasie-Kandidat neben echten Suchtreffern.
  const showCandidate = !exactHit && (isAddressQuery || hits.length === 0)

  function badgeFor(hitId: number, key: string): { label: string; className: string } | null {
    const ownStatus = ownStatusById.get(String(hitId))
    if (ownStatus === 'watched')
      return { label: t('add.results.watchingBadge'), className: 'pill-accent' }
    if (ownStatus === 'owned')
      return { label: t('add.results.ownedBadge'), className: 'pill-primary' }

    const doneStatus = done[key]
    if (doneStatus === 'watched')
      return { label: t('add.results.watchingBadge'), className: 'pill-success' }
    if (doneStatus === 'added')
      return { label: t('add.results.addedBadge'), className: 'pill-success' }

    return null
  }

  if (!isOpen) return null

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-hotspot-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose()
      }}
      data-testid="add-hotspot-modal"
    >
      <div className="modal">
        <div className="modal-header">
          <h2 id="add-hotspot-title">{t('add.title')}</h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label={t('add.closeLabel')}
            onClick={handleClose}
          >
            &times;
          </button>
        </div>

        <form
          onSubmit={(e) => {
            void handleSubmit(e)
          }}
        >
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="hotspot-name-or-address">
                {t('add.fieldLabel')} <span aria-hidden="true">{t('add.fieldRequired')}</span>
              </label>
              <input
                id="hotspot-name-or-address"
                className={`input${errors.name_or_address ? ' is-error' : ''}`}
                type="text"
                value={nameOrAddress}
                onChange={(e) => setNameOrAddress(e.target.value)}
                placeholder={t('add.placeholder')}
                aria-required="true"
                aria-describedby={errors.name_or_address ? 'addr-error' : undefined}
                data-testid="add-hotspot-input"
                autoFocus
              />
              {errors.name_or_address && (
                <span id="addr-error" className="field-error" role="alert">
                  {errors.name_or_address}
                </span>
              )}
            </div>

            {submitted && (
              <div data-testid="add-hotspot-results">
                <p className="muted" style={{ fontSize: 12, margin: '4px 0 8px' }}>
                  {t(isAddressQuery ? 'add.results.hintAddress' : 'add.results.hintName', {
                    count: hits.length,
                  })}
                </p>

                {hits.map((hit) => {
                  const badge = badgeFor(hit.id, hit.address)

                  return (
                    <div
                      style={RESULT_ROW_STYLE}
                      key={hit.id}
                      data-testid={`add-hotspot-hit-${hit.id}`}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>{hit.name}</div>
                        <div
                          className="mono muted"
                          style={{ fontSize: 11, wordBreak: 'break-all' }}
                        >
                          {hit.address}
                        </div>
                      </div>
                      {badge ? (
                        <span className={`pill ${badge.className}`}>{badge.label}</span>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              void handleAdd(true, hit.address, hit.address)
                            }}
                            disabled={addHotspot.isPending}
                            data-testid={`add-hotspot-hit-watch-${hit.id}`}
                          >
                            {t('add.results.watchBtn')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                              void handleAdd(false, hit.address, hit.address)
                            }}
                            disabled={addHotspot.isPending}
                            data-testid={`add-hotspot-hit-add-${hit.id}`}
                          >
                            {t('add.results.addBtn')}
                          </button>
                        </>
                      )}
                    </div>
                  )
                })}

                {showCandidate && (
                  <div style={RESULT_ROW_STYLE} data-testid="add-hotspot-candidate">
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{submitted}</div>
                      <div className="muted" style={{ fontSize: 11 }}>
                        {t(
                          isAddressQuery ? 'add.results.candidateNote' : 'add.results.notFoundHint',
                        )}
                      </div>
                      {!isAddressQuery && (
                        <div className="muted" style={{ fontSize: 11 }}>
                          {t('add.results.candidateNameHint')}
                        </div>
                      )}
                    </div>
                    {done.candidate ? (
                      <span className="pill pill-success" data-testid="add-hotspot-done">
                        {done.candidate === 'watched'
                          ? t('add.results.watchingBadge')
                          : t('add.results.addedBadge')}
                      </span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            void handleAdd(true, 'candidate', submitted)
                          }}
                          disabled={addHotspot.isPending}
                          data-testid="add-hotspot-watch"
                        >
                          {t('add.results.watchBtn')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            void handleAdd(false, 'candidate', submitted)
                          }}
                          disabled={addHotspot.isPending}
                          data-testid="add-hotspot-add"
                        >
                          {t('add.results.addBtn')}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {quota && (
              <div
                className="alert alert-warning"
                role="alert"
                data-testid="add-hotspot-quota-upsell"
                style={{
                  marginTop: 12,
                  padding: '10px 12px',
                  borderRadius: 6,
                  border: '1px solid var(--border)',
                  fontSize: 13,
                }}
              >
                <strong style={{ display: 'block', marginBottom: 4 }}>
                  {t('add.quota.title')}
                </strong>
                <p style={{ margin: 0 }}>
                  {quota.deviceType
                    ? t(`add.quota.description.${quota.deviceType}`)
                    : t('add.quota.description.generic')}
                </p>
                {quota.used !== undefined && quota.limit !== undefined && (
                  <p
                    className="muted"
                    style={{ margin: '4px 0 0' }}
                    data-testid="add-hotspot-quota-usage"
                  >
                    {t('add.quota.usage', { used: quota.used, limit: quota.limit })}
                  </p>
                )}
                {/*
                  upgrade_url des Backends ist '/premium/select_upgrade'; die
                  Splat-Route _auth.premium_.$ leitet das auf /plans um, daher
                  wird hier direkt typsicher dorthin verlinkt.
                */}
                <Link
                  to="/plans"
                  className="btn btn-sm btn-primary"
                  style={{ marginTop: 8 }}
                  onClick={handleClose}
                  data-testid="add-hotspot-quota-cta"
                >
                  {t('add.quota.cta')}
                </Link>
              </div>
            )}

            {errors.server && (
              <div className="field-error" role="alert" data-testid="add-hotspot-server-error">
                {errors.server}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleClose}
              disabled={addHotspot.isPending}
            >
              {t('add.cancelBtn')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={addHotspot.isPending}
              data-testid="add-hotspot-submit"
            >
              {search.isFetching ? t('add.submittingBtn') : t('add.searchBtn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
